import { useState, useEffect, useRef, Suspense } from "react";
import { lazyWithChunkReload } from "@/lib/lazy-with-chunk-reload";
import { Switch, Route, Link, useLocation, Redirect, Router } from "wouter";
import { queryClient } from "./lib/queryClient";
import { setImpersonatedUser, registerViewAsSetter } from "./lib/impersonation";
import { QueryClientProvider, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { getAvatarSource, getAvatarInitials } from "@/lib/avatar";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { ThemeToggle } from "@/components/theme-toggle";
import { AiCreditPill } from "@/components/ai-credit-pill";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { LiteUpsellBanner } from "@/components/lite-upsell-banner";
import { LiteCardRequiredGate } from "@/components/lite-card-required-gate";
import { DialerHost } from "@/components/dialer/dialer-host";
import { DialerHub } from "@/components/dialer/dialer-hub";
import { DialerListener } from "@/components/dialer/dialer-listener";
import { OnboardingWizard } from "@/components/onboarding-wizard";
import { UserSetupWizard } from "@/components/user-setup-wizard";
import { SecurityTrainingGate } from "@/components/security-training-gate";
import { TutorialHint } from "@/components/tutorial-hint";
import { GlobalGamificationCelebrations } from "@/components/gamification/global-celebrations";
import { BillingSuspendedGate } from "@/components/billing-suspended-gate";
import { TutorialModeProvider } from "@/contexts/tutorial-mode";
import { VoiceAssistantStateProvider, useVoiceAssistantState } from "@/contexts/voice-assistant-state";
import { CommandPaletteProvider } from "@/hooks/use-command-palette";
import { CommandPalette } from "@/components/command-palette";
import { VoiceAssistantWidget } from "@/components/voice-assistant/voice-assistant-widget";
import { NotificationBell } from "@/components/notification-bell";
import { InboxBell } from "@/components/inbox-bell";
import { useAuth } from "@/hooks/use-auth";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import { useSessionTracker } from "@/hooks/use-session-tracker";
import { WorkspaceProvider, getWorkspaceFromUrl, isDevelopmentMode } from "@/hooks/use-workspace";
import { useWorkspaceTimezone } from "@/hooks/use-workspace-timezone";
import { useIsMobile } from "@/hooks/use-mobile";
import { Loader2, Eye, X, LayoutDashboard, UserCircle, CalendarDays, BarChart3, ClipboardCheck, Calendar, ShieldAlert, Grid3X3, Users, FileText, DollarSign, Settings, Menu, LogOut, Sparkles, Mic2, ListMusic, SwitchCamera, MessageSquare, Phone } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { UserRole, UserProfile, Talent } from "@shared/schema";
import { getPermissionKeyForRoute, SIDEBAR_NAV_ITEMS, TOP_NAV_ITEMS, ARTIST_NAV_ITEMS, ADMIN_NAV_ITEMS, BETA_NAV_ITEMS } from "@shared/permissions/navigation";

import Landing from "@/pages/landing";
import AgencyVoicePage from "@/pages/agency-voice";
import Dashboard from "@/pages/dashboard";
import LeadsPage from "@/pages/leads";
import ClientsPage from "@/pages/clients";
import CustomersPage from "@/pages/customers";
import CustomerDetailPage from "@/pages/customer-detail";
import FormsPage from "@/pages/forms";
import InvoicesPage from "@/pages/invoices";
import BillingPage from "@/pages/billing";
import EventsPage from "@/pages/events";
import SchedulePage from "@/pages/schedule";
import MasterSchedulePage from "@/pages/master-schedule";
import TourDayPage from "@/pages/tour-day";
import LeadGenPage from "@/pages/lead-gen";
import LeadSandboxPage from "@/pages/lead-sandbox";
import MarketingContactsPage from "@/pages/marketing/contacts";
import MarketingNewsletterPage from "@/pages/marketing/newsletter";
import MarketingDistributionPage from "@/pages/marketing/distribution";
import MarketingAdsPage from "@/pages/marketing-ads";
import MarketingSocialPage from "@/pages/marketing/social";
import EmailHubPage from "@/pages/email-hub";
import InboxPage from "@/pages/inbox";
import ReportsPage from "@/pages/reports";
import SettingsPage from "@/pages/settings";
import { PlanBadge } from "@/components/plan-badge";
import UpgradePage from "@/pages/upgrade";
import MarketingPricingPage from "@/pages/marketing/pricing";
import MarketingFeaturesPage from "@/pages/marketing/features";
import MarketingAboutPage from "@/pages/marketing/about";
import { ErrorBoundary } from "@/components/error-boundary";
import UpgradeSuccessPage from "@/pages/upgrade-success";
import PublicInvoicePage from "@/pages/public-invoice";
import RequestEventPage from "@/pages/request-event";
import PublicBookingPage from "@/pages/public-booking";
import PublicReviewPage from "@/pages/public-review";
import PrivacyPolicyPage from "@/pages/privacy-policy";
import TermsOfServicePage from "@/pages/terms-of-service";
import DataDeletionPage from "@/pages/data-deletion";
import KnowledgePage from "@/pages/knowledge";
import { HelpPage, SecurityPage, TrustPage, PoliciesPage } from "@/pages/public-knowledge";
import HelpKbArticlePage from "@/pages/help-kb-article";
import AccountSecurityPage from "@/pages/security";
import AccountBillingPage from "@/pages/account-billing";
import MfaChallengePage from "@/pages/mfa-challenge";
import SignPage from "@/pages/sign";
import PlatformSignPage from "@/pages/platform-sign";
import PortalPage from "@/pages/portal";
import SuperAdminOnboardingPage from "@/pages/super-admin/onboarding";
import SuperAdminAiUsagePage from "@/pages/super-admin/ai-usage";
import SuperAdminWorkspaceHealthPage from "@/pages/super-admin/workspace-health";
import SuperAdminPanelPage from "@/pages/super-admin-panel";
import AdminLeadCatcherPage from "@/pages/admin-lead-catcher";
import FormBuilderPage from "@/pages/FormBuilderPage";
import RunsheetPage from "@/pages/runsheet";
import QuickLeadPage from "@/pages/quick-lead";
import ArtistPortalPage from "@/pages/artist-portal";
import PortalInvitePage from "@/pages/portal-invite";
import CallSheetEditorPage from "@/pages/call-sheet-editor";
import TalentCallsheetsPage from "@/pages/talent-callsheets";
import TalentContactsPage from "@/pages/talent-contacts";
import MyNumberPage from "@/components/backstage/my-number";
import TalentCalendarPage from "@/pages/talent-calendar";
import PublicCallSheetPage from "@/pages/public-call-sheet";
import PublicArtistPage from "@/pages/public-artist";
import AdminPressKitPage from "@/pages/admin-press-kit";
import { InstallPromptBanner } from "@/components/install-prompt";
import { PendingEmailDraftsProvider } from "@/hooks/use-pending-email-drafts";
import { GmailReconnectPrompt } from "@/components/gmail-reconnect-prompt";
import { GettingStartedDrawer } from "@/components/onboarding/getting-started-drawer";
import IntakePage from "@/pages/intake";
import ImportPage from "@/pages/import";
import SalesRepDashboard from "@/pages/sales-rep-dashboard";
import ManagerDashboardPage from "@/pages/manager-dashboard";
import ManagerPanelPage from "@/pages/manager-panel";
import AdminPage from "@/pages/admin";
import TeamActivityPage from "@/pages/team-activity";
import AgencyDashboardPage from "@/pages/agency-dashboard";
import SponsorHubPage from "@/pages/sponsor-hub";
import NotificationSettingsPage from "@/pages/notification-settings";
import NotificationsPage from "@/pages/notifications";
import FeedbackPage from "@/pages/feedback";
import TalentProfilePage from "@/pages/talent-profile";
import ProfilesPage from "@/pages/profiles";
import CalendarsPage from "@/pages/calendars";
import ConnectSmsPage from "@/pages/connect-sms";
import ConnectCalendarPage from "@/pages/connect-calendar";
import OAuthCallbackPage from "@/pages/oauth-callback";
import NotFound from "@/pages/not-found";
import EmailDraftRetryPage from "@/pages/email-draft-retry";
import VerifyEmail from "@/pages/VerifyEmail";

import PlatformLogin from "@/pages/platform/platform-login";
import PlatformDashboard from "@/pages/platform/platform-dashboard";
import PlatformFeedback from "@/pages/platform/platform-feedback";
import PlatformBackups from "@/pages/platform/platform-backups";
import PlatformEnhancements from "@/pages/platform/platform-enhancements";
import PlatformAdminHub from "@/pages/platform/platform-admin";
import PlatformWorkspacesHub from "@/pages/platform/platform-workspaces-hub";
import PlatformPlansHub from "@/pages/platform/platform-plans-hub";
import PlatformImportsHub from "@/pages/platform/platform-imports-hub";
import PlatformOperationsHub from "@/pages/platform/platform-operations-hub";
import PlatformSecurityHub from "@/pages/platform/platform-security-hub";
import PlatformConfigHub from "@/pages/platform/platform-config-hub";
import PlatformImportsMigrations from "@/pages/platform/platform-imports-migrations";
import PlatformAiImport from "@/pages/platform/platform-ai-import";
import PlatformAiAssistants from "@/pages/platform/platform-ai-assistants";
import PlatformAiAgents from "@/pages/platform/platform-ai-agents";
import PlatformAiAgentRuns from "@/pages/platform/platform-ai-agent-runs";
import PlatformEngineerReports from "@/pages/platform/platform-engineer-reports";
import { AiAgentsSurfaceProvider, TENANT_AI_AGENTS_SURFACE } from "@/pages/ai-agents-surface";
import PlatformAiIntelligence from "@/pages/platform/platform-ai-help-tool";
import PlatformSettings from "@/pages/platform/platform-settings";
import PlatformNotificationSettings from "@/pages/platform/platform-notification-settings";
import PlatformWorkspaces from "@/pages/platform/platform-workspaces";
import PlatformTiers from "@/pages/platform/platform-tiers";
import PlatformCoupons from "@/pages/platform/platform-coupons";
import PlatformAudit from "@/pages/platform/platform-audit";
import PlatformFinancialAudit from "@/pages/platform/platform-financial-audit";
import PlatformLeadAudit from "@/pages/platform/platform-lead-audit";
import PlatformEventAudit from "@/pages/platform/platform-event-audit";

import PlatformEncryptionMigration from "@/pages/platform/platform-encryption-migration";
import PlatformSeedFailures from "@/pages/platform/platform-seed-failures";
import PlatformSeedFailureAlerts from "@/pages/platform/platform-seed-failure-alerts";
import PlatformDevices from "@/pages/platform/platform-devices";
import PlatformPiiScans from "@/pages/platform/platform-pii-scans";
import PlatformHoneytokens from "@/pages/platform/platform-honeytokens";
import PlatformSecrets from "@/pages/platform/platform-secrets";
import PlatformEmailVerify from "@/pages/platform/platform-email-verify";
import PlatformWaitlist from "@/pages/platform/platform-waitlist";
import PlatformProfiles from "@/pages/platform/platform-profiles";
import PlatformCommunications from "@/pages/platform/platform-communications";
import PlatformBetaApprovals from "@/pages/platform/platform-beta-approvals";
import PlatformGmailRead from "@/pages/platform/platform-gmail-read";
import PlatformAccount from "@/pages/platform/platform-account";
import PlatformEmailSettings from "@/pages/platform/platform-email-settings";
import PlatformProspects from "@/pages/platform/platform-prospects";
import PlatformCustomers from "@/pages/platform/platform-customers";
import PlatformInvoices from "@/pages/platform/platform-invoices";
import PlatformBilling from "@/pages/platform/platform-billing";
import PlatformInfra from "@/pages/platform/platform-infra";
import PlatformEmailHub from "@/pages/platform/platform-email-hub";
import PlatformLeadGen from "@/pages/platform/platform-lead-gen";
import BetaApplyPage from "@/pages/beta-apply";
import PlatformIpAllowlist from "@/pages/platform/platform-ip-allowlist";
import PlatformCalls from "@/pages/platform/platform-calls";
import Signup from "@/pages/Signup";
import SignupSuccess from "@/pages/SignupSuccess";
import Login from "@/pages/Login";
import FirstLogin from "@/pages/FirstLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
// Task #128 — module-scope lazy import so the component is only created once,
// not re-instantiated on every render of the /select-workspace route.
const SelectWorkspacePage = lazyWithChunkReload(() => import("@/pages/SelectWorkspace"));

/**
 * ServerUnavailableBanner (Task #49)
 *
 * Polls /api/health every 30 s. Shows a banner the first time the server
 * becomes unreachable AFTER it was already reachable, so a cold-start 404
 * before the server is up does not flash a false-positive banner.
 * Dismisses automatically when the health check recovers.
 */
function ServerUnavailableBanner() {
  const [unavailable, setUnavailable] = useState(false);
  const everReachableRef = useRef(false);

  // Health-poll trigger: show banner when server was reachable and then goes dark.
  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch("/api/health", { credentials: "include" });
        if (!cancelled) {
          if (res.ok) {
            everReachableRef.current = true;
            setUnavailable(false);
          } else if (everReachableRef.current) {
            setUnavailable(true);
          }
        }
      } catch {
        if (!cancelled && everReachableRef.current) {
          setUnavailable(true);
        }
      }
    };

    check();
    const id = setInterval(check, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Query-failure trigger: show banner when 2+ paginated queries fail within 60 s.
  // Components dispatch "clarik:query-failure" whenever their isError flips true.
  useEffect(() => {
    const timestamps: number[] = [];
    const onFailure = () => {
      const now = Date.now();
      timestamps.push(now);
      while (timestamps.length > 0 && now - timestamps[0] > 60_000) timestamps.shift();
      if (timestamps.length >= 2 && everReachableRef.current) {
        setUnavailable(true);
      }
    };
    window.addEventListener("clarik:query-failure", onFailure);
    return () => window.removeEventListener("clarik:query-failure", onFailure);
  }, []);

  if (!unavailable) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[9999] flex items-center justify-center gap-3 bg-destructive text-destructive-foreground px-4 py-2 text-sm font-medium shadow-md"
      data-testid="banner-server-unavailable"
    >
      <span>Server unavailable — retrying automatically…</span>
    </div>
  );
}

/**
 * EmailVerificationBanner (Task #561)
 *
 * Shows a persistent amber banner for password-account users who haven't yet
 * verified their email. Hidden for Replit OIDC users (verified upstream) and
 * for accounts grandfathered before the feature launched.
 */
function EmailVerificationBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendFailed, setResendFailed] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const { data, isLoading } = useQuery<{ isVerified: boolean; isPasswordUser: boolean }>({
    queryKey: ["/api/auth/email-verification-status"],
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const handleResend = async () => {
    setResendLoading(true);
    setResendFailed(false);
    try {
      await apiRequest("POST", "/api/resend-verification-email");
      setResendSent(true);
    } catch {
      // Server returned a non-2xx (e.g. email send failed). Show a clear
      // error so the user knows to contact support rather than waiting
      // indefinitely for an email that never comes.
      setResendFailed(true);
    } finally {
      setResendLoading(false);
    }
  };

  if (isLoading || !data || data.isVerified || !data.isPasswordUser || dismissed) return null;

  return (
    <div
      className="flex items-center justify-between gap-3 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 px-4 py-2 text-sm text-amber-800 dark:text-amber-200"
      data-testid="banner-email-verification"
    >
      <span className="flex-1 min-w-0">
        {resendFailed ? (
          <><strong>Couldn't send the verification email.</strong> Please try again or contact support if this keeps happening.</>
        ) : resendSent ? (
          <><strong>Verification email sent!</strong> Check your inbox (and spam folder) for the link.</>
        ) : (
          <><strong>Please verify your email.</strong> Click &ldquo;Send verification email&rdquo; to receive a link.</>
        )}
      </span>
      <div className="flex items-center gap-2 shrink-0">
        {resendSent && !resendFailed ? (
          <span className="text-xs text-amber-700 dark:text-amber-300">Check your inbox ✓</span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50"
            onClick={handleResend}
            disabled={resendLoading}
            data-testid="button-resend-verification"
          >
            {resendLoading ? "Sending…" : resendFailed ? "Try again" : "Send verification email"}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50"
          onClick={() => setDismissed(true)}
          data-testid="button-dismiss-verification-banner"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function DynamicFavicon() {
  const [location] = useLocation();
  const { user } = useAuth();
  // Platform admin routes should never inherit workspace branding — they have
  // their own platform-default favicon set in super-admin settings. Workspace
  // colors and typography should also not bleed into the platform UI.
  const isPlatformRoute = location.startsWith("/platform");
  // Extract workspace slug from the URL so the branding query key is
  // workspace-scoped. Without this, TanStack Query serves cached branding from
  // whichever workspace was visited first — a cross-tenant data leak.
  const wsMatch = location.match(/^\/w\/([^/]+)/);
  const workspaceSlug = wsMatch ? wsMatch[1] : null;

  // Task #113 — Apply a distinctive "platform palette" data-attribute on
  // the document body whenever the user is on the Clarik (saas) workspace
  // shell. The accompanying CSS in index.css tints the sidebar/accents so
  // operators always know they're on the platform-control workspace
  // versus a customer tenant. Cleared on logout / tenant nav.
  const __wsTypeForPalette = ((user as any)?.workspaceType as "tenant" | "saas" | undefined) ?? null;
  useEffect(() => {
    const body = document.body;
    if (__wsTypeForPalette === "saas") {
      body.setAttribute("data-platform-palette", "clarik-saas");
    } else {
      body.removeAttribute("data-platform-palette");
    }
    return () => body.removeAttribute("data-platform-palette");
  }, [__wsTypeForPalette]);

  const { data: branding } = useQuery<{
    faviconPath: string | null;
    sidebarIconPath: string | null;
    bodyFontFamily?: string | null;
    displayFontFamily?: string | null;
    monoFontFamily?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    accentColor?: string | null;
  }>({
    queryKey: ["/api/branding/public", workspaceSlug],
    queryFn: async () => {
      const url = workspaceSlug
        ? `/api/branding/public?workspaceSlug=${encodeURIComponent(workspaceSlug)}`
        : `/api/branding/public`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !isPlatformRoute,
  });

  useEffect(() => {
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (!link) return;
    const base = "/api/favicon";
    if (isPlatformRoute) {
      // Platform panel: point to the platform-default favicon (no workspace
      // context in the URL so /api/favicon falls through to the platform default).
      link.href = `${base}?v=platform`;
      return;
    }
    // Always point to the server-side favicon endpoint with a version parameter
    // so every user gets the current workspace favicon without stale cache issues
    const version = branding?.faviconPath ? encodeURIComponent(branding.faviconPath) : "default";
    link.href = `${base}?v=${version}`;
  }, [branding?.faviconPath, isPlatformRoute]);

  // Task #13 (Clarik rebrand) — push per-workspace / platform-default
  // typography overrides into the CSS variables consumed by Tailwind's
  // font-family utilities. NULL on any field leaves the bundled default
  // (defined in `client/src/index.css`) in place.
  // Platform routes skip this entirely — the admin panel should always
  // render in Clarik's default palette/typeface, not a workspace's.
  useEffect(() => {
    const root = document.documentElement;
    if (isPlatformRoute) {
      root.style.removeProperty("--font-sans");
      root.style.removeProperty("--font-display");
      root.style.removeProperty("--font-mono");
      root.style.removeProperty("--primary");
      root.style.removeProperty("--secondary");
      root.style.removeProperty("--accent");
      return;
    }
    const apply = (varName: string, value: string | null | undefined) => {
      if (value && typeof value === "string" && value.trim()) {
        root.style.setProperty(varName, value);
      } else {
        root.style.removeProperty(varName);
      }
    };
    apply("--font-sans", branding?.bodyFontFamily);
    apply("--font-display", branding?.displayFontFamily);
    apply("--font-mono", branding?.monoFontFamily);
    // Task #13 — also push platform / workspace brand colors into the
    // CSS variables Tailwind reads. We accept #RRGGBB hex and convert
    // to the "H S% L%" space-separated form `index.css` uses (and
    // skip silently if the value isn't a valid hex). NULL on any
    // field leaves the bundled Clarik default in place.
    const hexToHsl = (hex: string): string | null => {
      const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
      if (!m) return null;
      const n = parseInt(m[1], 16);
      const r = (n >> 16 & 255) / 255, g = (n >> 8 & 255) / 255, b = (n & 255) / 255;
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      const l = (max + min) / 2;
      let h = 0, s = 0;
      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break;
          case g: h = (b - r) / d + 2; break;
          case b: h = (r - g) / d + 4; break;
        }
        h *= 60;
      }
      return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };
    const applyColor = (varName: string, hex: string | null | undefined) => {
      if (!hex) { root.style.removeProperty(varName); return; }
      const hsl = hexToHsl(hex);
      if (hsl) root.style.setProperty(varName, hsl);
    };
    applyColor("--primary", branding?.primaryColor);
    applyColor("--secondary", branding?.secondaryColor);
    applyColor("--accent", branding?.accentColor);
  }, [
    branding?.bodyFontFamily, branding?.displayFontFamily, branding?.monoFontFamily,
    branding?.primaryColor, branding?.secondaryColor, branding?.accentColor,
    isPlatformRoute,
  ]);

  return null;
}

interface UserWithProfile {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profile?: UserProfile;
}

function NotAuthorizedPage() {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      <ShieldAlert className="w-16 h-16 text-destructive mb-4" />
      <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
      <p className="text-muted-foreground text-center max-w-md">
        You don't have permission to access this page. Please contact your administrator if you believe this is an error.
      </p>
      <Link href="/">
        <Button className="mt-6" data-testid="button-go-home">
          Go to Dashboard
        </Button>
      </Link>
    </div>
  );
}

interface ProtectedRouteProps {
  permissionKey: string;
  permissions: Record<string, boolean>;
  children: React.ReactNode;
}

function ProtectedRoute({ permissionKey, permissions, children }: ProtectedRouteProps) {
  const hasPermission = permissions[permissionKey] === true;
  
  if (!hasPermission) {
    return <NotAuthorizedPage />;
  }
  
  return <>{children}</>;
}

interface AuthenticatedRouterProps {
  userId: string;
  viewAsUserId?: string;
}

// Task #113 — Removed ENTERTAINMENT_ONLY_PATH_PREFIXES. The Clarik
// workspace now ships every entertainment module so its admins can
// dogfood the full product. denyOnSaasWorkspace is also gone.

function AuthenticatedRouter({ userId, viewAsUserId }: AuthenticatedRouterProps) {
  const {
    permissions,
    role,
    userId: effectiveUserId,
    isLoading,
    isError,
    data: permissionsData,
    refetch,
    isFetching,
  } = useEffectivePermissions({ viewAsUserId });

  const effectiveRole = (role || "sales_rep") as UserRole;
  const actualUserId = userId;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // If the permissions endpoint actually failed, do NOT silently render the
  // app with an empty permission map — that turns every page into a misleading
  // "Access Denied". Show a real error with a retry instead.
  if (isError || !permissionsData) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8" data-testid="state-permissions-error">
        <ShieldAlert className="w-16 h-16 text-destructive mb-4" />
        <h1 className="text-2xl font-bold mb-2">We couldn't load your permissions</h1>
        <p className="text-muted-foreground text-center max-w-md">
          This usually means the server briefly couldn't reach the database. Your access hasn't changed — try again in a moment.
        </p>
        <Button
          className="mt-6"
          onClick={() => refetch()}
          disabled={isFetching}
          data-testid="button-retry-permissions"
        >
          {isFetching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Try again
        </Button>
      </div>
    );
  }

  return (
    <Switch>
      <Route path="/">
        {permissions["dashboard.module.visible"] !== true &&
        permissions["talent.module.visible"] === true ? (
          <Redirect
            to={
              permissions["talent.feature.callsheets"] === true
                ? "/talent/callsheets"
                : permissions["talent.feature.contacts"] === true
                  ? "/talent/contacts"
                  : permissions["talent.feature.calendar"] === true
                    ? "/talent/calendar"
                    : permissions["myEvents.module.visible"] === true
                      ? "/events"
                      : "/my-profile"
            }
          />
        ) : (
          <ProtectedRoute permissionKey="dashboard.module.visible" permissions={permissions}>
            <Dashboard 
              userRole={effectiveRole} 
              userId={effectiveUserId || actualUserId}
              isViewingAsUser={!!viewAsUserId}
              actualUserRole={effectiveRole}
            />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/my-progress">
        <ProtectedRoute permissionKey="dashboard.module.visible" permissions={permissions}>
          <SalesRepDashboard effectiveRole={effectiveRole} effectiveUserId={effectiveUserId || actualUserId} />
        </ProtectedRoute>
      </Route>
      <Route path="/leads">
        <ProtectedRoute permissionKey="leads.module.visible" permissions={permissions}>
          <LeadsPage effectiveRole={effectiveRole} effectiveUserId={effectiveUserId || actualUserId} />
        </ProtectedRoute>
      </Route>
      {/* Tenant-workspace AI Agents (Lead Outreach). Shares the platform
          components but injects the workspace-scoped surface (own API base +
          no PlatformLayout chrome). Gated by the per-tier aiAgents flag. The
          :agentId route is listed first so it wins over the bare list route. */}
      <Route path="/ai-agents/:agentId">
        <ProtectedRoute permissionKey="aiAgents.module.visible" permissions={permissions}>
          <AiAgentsSurfaceProvider value={TENANT_AI_AGENTS_SURFACE}>
            <PlatformAiAgentRuns />
          </AiAgentsSurfaceProvider>
        </ProtectedRoute>
      </Route>
      <Route path="/ai-agents">
        <ProtectedRoute permissionKey="aiAgents.module.visible" permissions={permissions}>
          <AiAgentsSurfaceProvider value={TENANT_AI_AGENTS_SURFACE}>
            <PlatformAiAgents />
          </AiAgentsSurfaceProvider>
        </ProtectedRoute>
      </Route>
      <Route path="/clients">
        <ProtectedRoute permissionKey="clients.module.visible" permissions={permissions}>
          <ClientsPage effectiveRole={effectiveRole} effectiveUserId={effectiveUserId || actualUserId} />
        </ProtectedRoute>
      </Route>
      {/* Task #102 — Clarik (saas) customers list. Nav entry is hidden on
          tenant workspaces; the page itself is permission-public so it
          doesn't 404 if a super-admin lands here while impersonating. */}
      <Route path="/customers">
        <CustomersPage />
      </Route>
      <Route path="/customers/:id">
        <CustomerDetailPage />
      </Route>
      <Route path="/forms">
        <ProtectedRoute permissionKey="forms.module.visible" permissions={permissions}>
          <FormsPage effectiveRole={effectiveRole} effectiveUserId={effectiveUserId || actualUserId} />
        </ProtectedRoute>
      </Route>
      <Route path="/invoices">
        <ProtectedRoute permissionKey="invoices.module.visible" permissions={permissions}>
          <InvoicesPage effectiveRole={effectiveRole} effectiveUserId={effectiveUserId || actualUserId} />
        </ProtectedRoute>
      </Route>
      <Route path="/billing">
        <ProtectedRoute permissionKey="billing.module.visible" permissions={permissions}>
          <BillingPage />
        </ProtectedRoute>
      </Route>
      <Route path="/feedback">
        <FeedbackPage />
      </Route>
      <Route path="/reports">
        <ProtectedRoute permissionKey="reports.module.visible" permissions={permissions}>
          <ReportsPage effectiveRole={effectiveRole} />
        </ProtectedRoute>
      </Route>
      <Route path="/master-schedule">
        <ProtectedRoute permissionKey="schedule.module.visible" permissions={permissions}>
          <MasterSchedulePage effectiveRole={effectiveRole} />
        </ProtectedRoute>
      </Route>
      <Route path="/calendars">
        <ProtectedRoute permissionKey="calendarHub.module.visible" permissions={permissions}>
          <CalendarsPage />
        </ProtectedRoute>
      </Route>
      {/* Task #3 — legacy /conflict-radar bookmarks/notifications now
          land on the Calendar Hub conflicts panel. */}
      <Route path="/conflict-radar">
        <Redirect to="/calendars?panel=conflicts" />
      </Route>
      <Route path="/lead-gen">
        <ProtectedRoute permissionKey="leadGen.module.visible" permissions={permissions}>
          <LeadGenPage effectiveUserId={effectiveUserId || actualUserId} />
        </ProtectedRoute>
      </Route>
      <Route path="/lead-sandbox">
        <ProtectedRoute permissionKey="leadGen.module.visible" permissions={permissions}>
          <LeadSandboxPage />
        </ProtectedRoute>
      </Route>
      <Route path="/marketing/contacts">
        <ProtectedRoute permissionKey="marketing.feature.viewContacts" permissions={permissions}>
          <MarketingContactsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/marketing/newsletter">
        <ProtectedRoute permissionKey="marketing.feature.viewNewsletter" permissions={permissions}>
          <MarketingNewsletterPage />
        </ProtectedRoute>
      </Route>
      <Route path="/marketing/distribution">
        <ProtectedRoute permissionKey="marketing.feature.viewDistribution" permissions={permissions}>
          <MarketingDistributionPage />
        </ProtectedRoute>
      </Route>
      <Route path="/marketing/ads">
        <ProtectedRoute permissionKey="marketing.feature.viewAds" permissions={permissions}>
          <MarketingAdsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/marketing/social">
        <ProtectedRoute permissionKey="socialMedia.module.visible" permissions={permissions}>
          <MarketingSocialPage />
        </ProtectedRoute>
      </Route>
      <Route path="/email-hub">
        <ProtectedRoute permissionKey="communications.module.visible" permissions={permissions}>
          <EmailHubPage effectiveRole={effectiveRole} effectiveUserId={effectiveUserId || actualUserId} />
        </ProtectedRoute>
      </Route>
      <Route path="/inbox">
        <ProtectedRoute permissionKey="communications.module.visible" permissions={permissions}>
          <InboxPage />
        </ProtectedRoute>
      </Route>
      <Route path="/intake">
        <ProtectedRoute permissionKey="approvals.module.visible" permissions={permissions}>
          <IntakePage effectiveRole={effectiveRole} />
        </ProtectedRoute>
      </Route>
      <Route path="/events">
        <ProtectedRoute permissionKey="myEvents.module.visible" permissions={permissions}>
          <EventsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/schedule">
        <ProtectedRoute permissionKey="myEvents.module.visible" permissions={permissions}>
          <SchedulePage />
        </ProtectedRoute>
      </Route>
      <Route path="/my-profile">
        <ProtectedRoute permissionKey="myProfile.module.visible" permissions={permissions}>
          <TalentProfilePage />
        </ProtectedRoute>
      </Route>
      <Route path="/tour-day">
        <ProtectedRoute permissionKey="myProfile.module.visible" permissions={permissions}>
          <TourDayPage />
        </ProtectedRoute>
      </Route>
      <Route path="/import">
        <ProtectedRoute permissionKey="leads.feature.import" permissions={permissions}>
          <ImportPage effectiveRole={effectiveRole} />
        </ProtectedRoute>
      </Route>
      <Route path="/notifications" component={NotificationsPage} />
      <Route path="/settings/notifications" component={NotificationSettingsPage} />
      <Route path="/settings">
        <SettingsPage effectiveRole={effectiveRole} effectiveUserId={effectiveUserId || actualUserId} isViewingAsUser={!!viewAsUserId} />
      </Route>
      <Route path="/upgrade" component={UpgradePage} />
      {/* Task #124 — The in-tenant Super Admin Panel hub was deleted
          along with the /w/clarik shell. The /super-admin/* surfaces
          now live under /platform/* with their own PlatformLayout
          chrome; these tenant routes redirect there so any stale
          bookmark resolves cleanly. */}
      <Route path="/super-admin" component={() => <Redirect to="/platform/dashboard" />} />
      <Route path="/super-admin/onboarding" component={() => <Redirect to="/platform/dashboard" />} />
      <Route path="/super-admin/ai-usage" component={() => <Redirect to="/platform/ai-intelligence" />} />
      <Route path="/super-admin/workspace-health" component={() => <Redirect to="/platform/operations" />} />
      <Route path="/runsheet" component={RunsheetPage} />
      <Route path="/quick-lead" component={QuickLeadPage} />
      <Route path="/artist" component={ArtistPortalPage} />
      <Route path="/talent/callsheets">
        <ProtectedRoute permissionKey="talent.feature.callsheets" permissions={permissions}>
          <TalentCallsheetsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/talent/contacts">
        <ProtectedRoute permissionKey="talent.feature.contacts" permissions={permissions}>
          <TalentContactsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/talent/my-number" component={MyNumberPage} />
      <Route path="/talent/calendar">
        <ProtectedRoute permissionKey="talent.feature.calendar" permissions={permissions}>
          <TalentCalendarPage />
        </ProtectedRoute>
      </Route>
      <Route path="/portal-invite/:token" component={PortalInvitePage} />
      <Route path="/admin/press-kit">
        <ProtectedRoute permissionKey="adminPanel.feature.manageTalent" permissions={permissions}>
          <AdminPressKitPage />
        </ProtectedRoute>
      </Route>
      <Route path="/admin/form-builder/:formId">
        <ProtectedRoute permissionKey="adminPanel.feature.manageTalent" permissions={permissions}>
          <FormBuilderPage />
        </ProtectedRoute>
      </Route>
      <Route path="/call-sheet/:id" component={CallSheetEditorPage} />
      <Route path="/upgrade/success" component={UpgradeSuccessPage} />
      <Route path="/profiles">
        <ProtectedRoute permissionKey="profiles.feature.view" permissions={permissions}>
          <ProfilesPage effectiveRole={effectiveRole} effectiveUserId={effectiveUserId || actualUserId} />
        </ProtectedRoute>
      </Route>
      <Route path="/admin/team-activity">
        <ProtectedRoute permissionKey="adminPanel.feature.activityTracker" permissions={permissions}>
          <TeamActivityPage />
        </ProtectedRoute>
      </Route>
      <Route path="/admin">
        <ProtectedRoute permissionKey="adminPanel.module.visible" permissions={permissions}>
          <AdminPage />
        </ProtectedRoute>
      </Route>
      <Route path="/admin/voice">
        <ProtectedRoute permissionKey="adminPanel.feature.brandingSettings" permissions={permissions}>
          <AgencyVoicePage />
        </ProtectedRoute>
      </Route>
      {/* Task #599 — AI Lead Catcher dedicated admin page */}
      <Route path="/admin/lead-catcher">
        <ProtectedRoute permissionKey="adminPanel.module.visible" permissions={permissions}>
          <AdminLeadCatcherPage />
        </ProtectedRoute>
      </Route>
      {/* Task #586 — Agency Dashboard. The page itself enforces the gate
          (404 fallback) so this route doesn't go through the normal
          permission system. */}
      <Route path="/agency" component={AgencyDashboardPage} />
      {/* Task #205 — Sponsor Hub. Permission-gated by marketing.feature.viewSponsorHub;
          page also enforces the sponsorHubEnabled workspace flag internally. */}
      <Route path="/sponsor-hub">
        <ProtectedRoute permissionKey="marketing.feature.viewSponsorHub" permissions={permissions}>
          <SponsorHubPage />
        </ProtectedRoute>
      </Route>
      <Route path="/knowledge/:slug">
        <ProtectedRoute permissionKey="knowledge.module.visible" permissions={permissions}>
          <KnowledgePage />
        </ProtectedRoute>
      </Route>
      <Route path="/knowledge">
        <ProtectedRoute permissionKey="knowledge.module.visible" permissions={permissions}>
          <KnowledgePage />
        </ProtectedRoute>
      </Route>
      {/* Task #703 — Manager Dashboard. The page itself enforces the
          isManager gate (renders NotAuthorized for non-managers) so
          this route doesn't go through the permission system. */}
      <Route path="/team" component={ManagerDashboardPage} />
      {/* Task #704 — Manager Panel. Page enforces its own isManager gate. */}
      <Route path="/manager-panel" component={ManagerPanelPage} />
      <Route path="/email-drafts/:draftId" component={EmailDraftRetryPage} />
      <Route path="/account/security" component={AccountSecurityPage} />
      <Route path="/account/billing" component={AccountBillingPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

interface HeaderNavProps {
  permissions: Record<string, boolean>;
}

function HeaderNav({ permissions }: HeaderNavProps) {
  const [location] = useLocation();
  const { user } = useAuth();

  const { data: approvalCount } = useQuery<{ count: number; pendingRequests: number; submissions: number }>({
    queryKey: ["/api/approval-notifications/count"],
    staleTime: 60000,
  });

  // Task #102 — Also filter top nav for saas workspaces in this older
  // pre-mobile path.
  const __wsType = ((user as any)?.workspaceType as "tenant" | "saas" | undefined) ?? "tenant";
  const visibleItems = TOP_NAV_ITEMS.filter(
    (item) =>
      !(item.hideOnWorkspaceType?.includes(__wsType)) &&
      (!item.showOnlyOnWorkspaceType || item.showOnlyOnWorkspaceType.includes(__wsType)),
  ).filter(
    // Calendar Hub now lives in the right-side icon cluster next to the
    // email hub (InboxBell). Hide it here so it doesn't render twice.
    (item) => permissions[item.permissionKey] === true && item.id !== "calendarHub"
  );

  // Task #703 — Team Dashboard top-nav entry. Gated on the server-side
  // `canSeeTeam` flag, which is strictly `isManager=true` per the
  // acceptance criteria. Admins can still reach the data directly via
  // /team (resolveManagerCtx grants admin override on the data routes),
  // but the nav button itself is manager-only to avoid muddying the
  // admin UX with a team rollup they don't manage.
  const { data: managerMe } = useQuery<{ canSeeTeam: boolean }>({
    queryKey: ["/api/manager-dashboard/me"],
    staleTime: 5 * 60_000,
  });

  // Map top-nav item ids to tutorial hint registry ids. Items missing from
  // this map render no hint icon.
  const topNavHintIds: Record<string, string> = {
    schedule: "sidebar.masterSchedule",
    communications: "sidebar.emailHub",
  };

  return (
    <nav className="hidden md:flex items-center gap-1" data-testid="header-nav">
      {visibleItems.map((item) => {
        const isActive = location === item.url;
        const Icon = item.icon;
        const hintId = topNavHintIds[item.id];
        return (
          <div key={item.url} className="flex items-center">
            <Link href={item.url}>
              <Button
                variant={isActive ? "secondary" : "ghost"}
                size="sm"
                className="gap-1.5 h-8 relative"
                data-testid={`header-nav-${item.id}`}
                aria-label={item.id === "calendarHub" ? item.title : undefined}
                title={item.id === "calendarHub" ? item.title : undefined}
              >
                <Icon className="h-4 w-4" />
                {item.id !== "calendarHub" && (
                  <span className="hidden sm:inline">{item.title}</span>
                )}
                {item.badgeKey === "approvalCount" && approvalCount?.count && approvalCount.count > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-1 -right-1 h-5 min-w-5 px-1.5 text-xs flex items-center justify-center"
                    data-testid="header-badge-approval-count"
                  >
                    {approvalCount.count}
                  </Badge>
                )}
              </Button>
            </Link>
            {hintId && <TutorialHint id={hintId} className="ml-0.5" />}
          </div>
        );
      })}
      {managerMe?.canSeeTeam && (
        <Link href="/team">
          <Button
            variant={location === "/team" ? "secondary" : "ghost"}
            size="sm"
            className="gap-1.5 h-8"
            data-testid="header-nav-team"
          >
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Team</span>
          </Button>
        </Link>
      )}
    </nav>
  );
}

interface MobileBottomNavProps {
  permissions: Record<string, boolean>;
  user?: { firstName?: string | null; lastName?: string | null; email?: string | null; profileImageUrl?: string | null } | null;
  talentView?: boolean;
  linkedTalent?: Talent | null;
  isAdmin?: boolean;
  viewAsUserId?: string;
  setViewAsUserId?: (v: string) => void;
  allUsers?: UserWithProfile[];
}

// Center AI button that lives in the middle slot of the bottom nav.
function NavCenterAIButton() {
  // Task #427 — spoken voice is off for beta. This is a plain typed-chat
  // launcher: it always opens the assistant drawer, no mic / VAD visuals.
  const { setOpen } = useVoiceAssistantState();

  return (
    <div className="flex-1 flex items-center justify-center" style={{ marginTop: "-18px" }}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open AI assistant"
        data-testid="button-mobile-nav-ai-center"
        className="h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center relative transition-shadow border-4 border-background"
      >
        <Sparkles className="w-6 h-6" />
      </button>
    </div>
  );
}

function MobileBottomNav({ permissions, user, talentView, linkedTalent, isAdmin, viewAsUserId, setViewAsUserId, allUsers }: MobileBottomNavProps) {
  const [location] = useLocation();

  // Admin view: Dashboard, Leads, [AI], Clients, More
  const ADMIN_LEFT_SLOTS = [
    { id: "dashboard", title: "Dashboard", url: "/", icon: LayoutDashboard, permissionKey: "dashboard.module.visible" },
    { id: "leads", title: "Leads", url: "/leads", icon: Grid3X3, permissionKey: "leads.module.visible" },
  ];
  const ADMIN_RIGHT_SLOTS = [
    { id: "clients", title: "Clients", url: "/clients", icon: Users, permissionKey: "clients.module.visible" },
  ];

  // Talent view: Dashboard, My Profile, [AI], My Events, More
  const TALENT_LEFT_SLOTS = [
    { id: "dashboard", title: "Dashboard", url: "/", icon: LayoutDashboard, permissionKey: "dashboard.module.visible" },
    { id: "myProfile", title: "My Profile", url: "/my-profile", icon: Mic2, permissionKey: "myProfile.module.visible" },
  ];
  const TALENT_RIGHT_SLOTS = [
    { id: "myEvents", title: "My Events", url: "/events", icon: ListMusic, permissionKey: "myEvents.module.visible" },
  ];

  const leftSlots = talentView ? TALENT_LEFT_SLOTS : ADMIN_LEFT_SLOTS;
  const rightSlots = talentView ? TALENT_RIGHT_SLOTS : ADMIN_RIGHT_SLOTS;

  const filterVisible = (slots: typeof ADMIN_LEFT_SLOTS) =>
    slots.filter((s) => permissions[s.permissionKey] === true || s.permissionKey === "always_visible");

  const visibleLeft = filterVisible(leftSlots);
  const visibleRight = filterVisible(rightSlots);

  const renderSlot = (item: { id: string; title: string; url: string; icon: React.ElementType }) => {
    const isActive = location === item.url || (item.url !== "/" && location.startsWith(item.url + "/"));
    const Icon = item.icon;
    return (
      <Link key={item.url} href={item.url} className="flex-1">
        <div
          className={`flex flex-col items-center justify-center py-2 px-1 min-h-[56px] ${isActive ? "text-primary" : "text-muted-foreground"}`}
          data-testid={`mobile-nav-${item.id}`}
        >
          <Icon className="h-5 w-5 mb-1" />
          <span className="text-[10px] font-medium truncate">{item.title}</span>
        </div>
      </Link>
    );
  };

  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border flex items-center safe-area-inset-bottom"
      data-testid="mobile-bottom-nav"
    >
      {visibleLeft.map(renderSlot)}
      <NavCenterAIButton />
      {visibleRight.map(renderSlot)}
      {talentView ? (
        <TalentMobileMoreMenu
          permissions={permissions}
          user={user}
          isAdmin={isAdmin}
          viewAsUserId={viewAsUserId}
          setViewAsUserId={setViewAsUserId}
          allUsers={allUsers}
        />
      ) : (
        <MobileMoreMenu permissions={permissions} visiblePrimaryCount={3} user={user} />
      )}
    </nav>
  );
}

interface TalentMobileMoreMenuProps {
  permissions: Record<string, boolean>;
  user?: { firstName?: string | null; lastName?: string | null; email?: string | null; profileImageUrl?: string | null } | null;
  isAdmin?: boolean;
  viewAsUserId?: string;
  setViewAsUserId?: (v: string) => void;
  allUsers?: UserWithProfile[];
}

function TalentMobileMoreMenu({ permissions, user, isAdmin, viewAsUserId, setViewAsUserId, allUsers }: TalentMobileMoreMenuProps) {
  const [open, setOpen] = useState(false);
  const [location] = useLocation();

  const impersonatedUser = viewAsUserId && allUsers ? allUsers.find((u) => u.id === viewAsUserId) : null;

  const { data: meProfile } = useQuery<{ profile: { profilePicture: string | null } | null }>({
    queryKey: ["/api/profiles/me"],
    enabled: !!user,
  });
  const avatarSrc = getAvatarSource({
    profilePicture: meProfile?.profile?.profilePicture,
    profileImageUrl: user?.profileImageUrl,
  });
  const avatarFallback = getAvatarInitials(
    user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.firstName || user?.email || "User",
  );

  const menuItems = [
    { id: "callsheets", title: "Call Sheets", url: "/artist", icon: ClipboardCheck },
    { id: "mynumber", title: "My Number", url: "/talent/my-number", icon: Phone },
    { id: "settings", title: "Settings", url: "/settings", icon: Settings },
  ];

  return (
    <>
      <button
        className="flex-1 flex flex-col items-center justify-center py-2 px-1 min-h-[56px] text-muted-foreground"
        onClick={() => setOpen(true)}
        data-testid="mobile-talent-nav-more"
      >
        <Menu className="h-5 w-5 mb-1" />
        <span className="text-[10px] font-medium">More</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="h-auto max-h-[70vh] overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Menu</SheetTitle>
          </SheetHeader>
          {isAdmin && impersonatedUser && setViewAsUserId && (
            <div className="mb-4 px-3 py-2 bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700 rounded-lg flex items-center gap-2" data-testid="talent-more-impersonation-banner">
              <Eye className="h-3 w-3 text-orange-600 dark:text-orange-400 shrink-0" />
              <span className="text-xs font-medium text-orange-700 dark:text-orange-300 flex-1 truncate">
                Acting as: {impersonatedUser.firstName} {impersonatedUser.lastName}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-orange-700 dark:text-orange-300 shrink-0"
                onClick={() => { setViewAsUserId(""); setOpen(false); }}
                data-testid="button-exit-impersonation-talent-more"
              >
                <X className="h-3 w-3 mr-1" />
                Exit
              </Button>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            {menuItems.map((item) => {
              const isActive = location === item.url || location.startsWith(item.url + "/");
              const Icon = item.icon;
              return (
                <Link key={item.url} href={item.url} onClick={() => setOpen(false)}>
                  <div
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border min-h-[72px] ${isActive ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground"}`}
                    data-testid={`mobile-talent-more-nav-${item.id}`}
                  >
                    <Icon className="h-6 w-6 mb-1" />
                    <span className="text-xs font-medium text-center">{item.title}</span>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-border space-y-2">
            {user && (
              <div className="flex items-center gap-3 px-2 py-2">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={avatarSrc} />
                  <AvatarFallback>{avatarFallback}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email || "User"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
              </div>
            )}
            <button
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg border border-border text-destructive min-h-[48px]"
              onClick={() => { setOpen(false); window.location.href = "/api/logout"; }}
              data-testid="mobile-talent-more-logout"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

interface MobileMoreMenuProps {
  permissions: Record<string, boolean>;
  visiblePrimaryCount: number;
  user?: { firstName?: string | null; lastName?: string | null; email?: string | null; profileImageUrl?: string | null } | null;
}

function MobileMoreMenu({ permissions, visiblePrimaryCount, user }: MobileMoreMenuProps) {
  const [open, setOpen] = useState(false);
  const [location] = useLocation();
  const { setOpen: setVAOpen } = useVoiceAssistantState();
  const { data: vaConfig } = useQuery<{ settings: { enabled?: boolean; enabledRoles?: string[]; mobileButtonMode?: string } | null; role?: string }>({
    queryKey: ["/api/voice-assistant/config"],
    staleTime: 5 * 60 * 1000,
  });
  const vaEnabled = !!vaConfig?.settings?.enabled &&
    !!(vaConfig?.settings?.enabledRoles ?? []).includes(vaConfig?.role ?? "");

  // Pull the personal profile so the mobile menu shows the company-managed
  // avatar (same source as the sidebar / My Profile) instead of falling
  // straight to the auth provider's photo.
  const { data: meProfile } = useQuery<{ profile: { profilePicture: string | null } | null }>({
    queryKey: ["/api/profiles/me"],
    enabled: !!user,
  });
  const mobileMenuAvatarSrc = getAvatarSource({
    profilePicture: meProfile?.profile?.profilePicture,
    profileImageUrl: user?.profileImageUrl,
  });
  const mobileMenuFallback = getAvatarInitials(
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firstName || user?.email || "User",
  );

  // Task #102 — workspaceType comes from /api/auth/user; entertainment-only
  // modules are hidden when the active workspace is 'saas' (Clarik itself).
  const workspaceType = ((user as any)?.workspaceType as "tenant" | "saas" | undefined) ?? "tenant";
  const passesType = (item: any) => {
    if (item.hideOnWorkspaceType?.includes(workspaceType)) return false;
    if (item.showOnlyOnWorkspaceType && !item.showOnlyOnWorkspaceType.includes(workspaceType)) return false;
    return true;
  };
  const passesPerm = (item: any) =>
    item.permissionKey === "always_visible" || permissions[item.permissionKey] === true;

  // Primary sidebar items (first 4 shown in bottom nav)
  const primaryItems = SIDEBAR_NAV_ITEMS.filter(
    (item) => passesPerm(item) && passesType(item),
  ).slice(0, visiblePrimaryCount);
  const primaryUrls = new Set(primaryItems.map(i => i.url));

  // Secondary sidebar items (overflow from bottom nav)
  const secondarySidebarItems = SIDEBAR_NAV_ITEMS.filter(
    (item) => passesPerm(item) && passesType(item),
  ).slice(visiblePrimaryCount);

  // Top nav items (currently hidden on mobile)
  const topNavItems = TOP_NAV_ITEMS.filter(
    (item) => passesPerm(item) && passesType(item),
  );

  // Artist nav items
  const artistNavItems = ARTIST_NAV_ITEMS.filter(
    (item) => passesPerm(item) && passesType(item),
  );

  // Admin nav items
  const adminNavItems = ADMIN_NAV_ITEMS.filter(
    (item) => passesPerm(item) && passesType(item),
  );

  // Approvals inbox count for the Manager Panel badge (task #712).
  // Only fires when the user can actually see the Manager Panel entry,
  // so non-managers don't poll the endpoint. Mirrors the sidebar query
  // key so a decide-mutation invalidation refreshes both surfaces.
  const canSeeManagerPanel = adminNavItems.some(
    (item) => item.badgeKey === "approvalsInboxCount"
  );
  const { data: approvalsInbox } = useQuery<{ count: number }>({
    queryKey: ["/api/approvals/inbox-count"],
    enabled: canSeeManagerPanel,
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const approvalsInboxCount = approvalsInbox?.count ?? 0;

  // Beta nav items (always visible)
  const betaNavItems = BETA_NAV_ITEMS.filter(
    (item) => item.permissionKey === "always_visible" || permissions[item.permissionKey] === true
  );

  // All items for the "More" sheet (exclude primary bottom nav items).
  // artistNavItems (My Profile, My Events) are talent-only — they live in
  // the talent bottom nav and must not appear in the admin Navigation sheet.
  const moreItems = [
    ...secondarySidebarItems,
    ...topNavItems,
    ...adminNavItems,
    ...betaNavItems,
  ].filter(item => !primaryUrls.has(item.url));

  // Add settings manually
  const settingsItem = { id: "settings", title: "Settings", url: "/settings", icon: Settings, permissionKey: "always_visible" };

  // Map nav item ids to tutorial hint registry ids so the "More" sheet
  // surfaces the same help icons as the desktop header / sidebar. Items
  // missing from this map render no hint.
  const moreNavHintIds: Record<string, string> = {
    schedule: "sidebar.masterSchedule",
    communications: "sidebar.emailHub",
    leads: "sidebar.leads",
    clients: "sidebar.clients",
    forms: "sidebar.forms",
    invoices: "sidebar.invoices",
  };

  return (
    <>
      <button
        className="flex-1 flex flex-col items-center justify-center py-2 px-1 min-h-[56px] text-muted-foreground"
        onClick={() => setOpen(true)}
        data-testid="mobile-nav-more"
      >
        <Menu className="h-5 w-5 mb-1" />
        <span className="text-[10px] font-medium">More</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="h-auto max-h-[70vh] overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Navigation</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-3 gap-3 pb-safe">
            {vaEnabled && (
              <button
                type="button"
                onClick={() => { setOpen(false); setVAOpen(true); }}
                data-testid="mobile-more-nav-ai-assistant"
                className="flex flex-col items-center justify-center p-3 rounded-lg border min-h-[72px] border-border text-muted-foreground"
              >
                <MessageSquare className="h-6 w-6 mb-1" />
                <span className="text-xs font-medium text-center">AI Assistant</span>
              </button>
            )}
            {moreItems.map((item) => {
              const isActive = location === item.url || location.startsWith(item.url + "/");
              const Icon = item.icon || LayoutDashboard;
              const hintId = moreNavHintIds[item.id];
              const badgeCount =
                item.badgeKey === "approvalsInboxCount" ? approvalsInboxCount : 0;
              return (
                <div key={item.url} className="relative">
                  <Link href={item.url} onClick={() => setOpen(false)}>
                    <div className={`flex flex-col items-center justify-center p-3 rounded-lg border min-h-[72px] ${isActive ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground"}`} data-testid={`mobile-more-nav-${item.id}`}>
                      <Icon className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium text-center">{item.title}</span>
                    </div>
                  </Link>
                  {badgeCount > 0 && (
                    <span
                      className="absolute -top-1 -left-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground"
                      data-testid={`mobile-more-badge-${item.id}-count`}
                    >
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                  {hintId && (
                    <div className="absolute top-1 right-1">
                      <TutorialHint id={hintId} />
                    </div>
                  )}
                </div>
              );
            })}
            <Link href={settingsItem.url} onClick={() => setOpen(false)}>
              <div className={`flex flex-col items-center justify-center p-3 rounded-lg border min-h-[72px] ${location === settingsItem.url ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground"}`} data-testid="mobile-more-nav-settings">
                <Settings className="h-6 w-6 mb-1" />
                <span className="text-xs font-medium text-center">Settings</span>
              </div>
            </Link>
          </div>
          {/* User account section */}
          <div className="mt-4 pt-4 border-t border-border space-y-2">
            {user && (
              <div className="flex items-center gap-3 px-2 py-2">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={mobileMenuAvatarSrc} />
                  <AvatarFallback>{mobileMenuFallback}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email || "User"}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
              </div>
            )}
            <button
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg border border-border text-destructive min-h-[48px]"
              onClick={() => { setOpen(false); window.location.href = "/api/logout"; }}
              data-testid="mobile-more-logout"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// Task #253 — when AuthenticatedApp finds no user at a deep `/w/<slug>/...`
// URL, we must NOT render the public marketing Landing (that strands an
// authenticated user on the marketing page at their own workspace URL after
// an OAuth round-trip). Instead, hard-redirect to login carrying a returnTo so
// the user lands right back where they were once the session is re-confirmed.
// A genuine session blip recovers automatically because /login re-checks auth
// and forwards straight to returnTo without showing the form.
// How many consecutive LoginRedirect bounces we allow before giving up and
// showing the login form with ?fresh=1 (which prevents the Login page from
// auto-forwarding back, breaking any loop). Reset when the user successfully
// navigates to a workspace route.
const LOGIN_REDIRECT_MAX = 3;
const LOGIN_REDIRECT_KEY = "login_redirect_count_v1";

function LoginRedirect() {
  useEffect(() => {
    const returnTo = window.location.pathname + window.location.search;

    // Count consecutive redirects to detect the mobile flash loop.
    // The count is kept in sessionStorage so it survives the location.replace
    // but is cleared when the user closes the tab or reaches the login page.
    const prev = parseInt(sessionStorage.getItem(LOGIN_REDIRECT_KEY) || "0", 10);
    const next = prev + 1;
    sessionStorage.setItem(LOGIN_REDIRECT_KEY, String(next));

    if (next >= LOGIN_REDIRECT_MAX) {
      // Too many bounces — the session is not sticking. Send with ?fresh=1 so
      // the Login page won't auto-forward back and the user can type credentials.
      sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
      window.location.replace(`/login?fresh=1`);
    } else {
      window.location.replace(`/login?returnTo=${encodeURIComponent(returnTo)}`);
    }
  }, []);
  return (
    <div className="flex items-center justify-center h-screen">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function AuthenticatedAppShell() {
  return (
    <TutorialModeProvider>
      <AuthenticatedApp />
    </TutorialModeProvider>
  );
}

function AuthenticatedApp() {
  const { user, isLoading: authLoading } = useAuth();
  const [viewAsUserId, setViewAsUserId] = useState<string>("");
  const [talentViewActive, setTalentViewActive] = useState(false);
  const [dialerHubOpen, setDialerHubOpen] = useState(false);

  // Permissions must be declared here — before any hook or query that
  // references them — so the dialer visibility gate below has access to
  // the resolved permission value without a Temporal Dead Zone crash.
  const { permissions: earlyPermissions } = useEffectivePermissions({
    viewAsUserId: viewAsUserId || undefined,
    enabled: !!user,
  });

  const { data: dialerSettings } = useQuery<{ dialerEnabled?: boolean }>({
    queryKey: ["/api/settings/dialer"],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
  const dialerEnabled = !!dialerSettings?.dialerEnabled;

  // Show the dialer hub when the user has the explicit permission OR when the
  // workspace has dialer enabled (fallback for workspaces that haven't yet
  // migrated to the granular permission grant). Declared AFTER dialerEnabled
  // to avoid a Temporal Dead Zone reference.
  const dialerHubVisible = earlyPermissions["dialer.hub.visible"] === true || dialerEnabled;

  const { data: dialerUnreadCounts } = useQuery<{ missedCalls: number; voicemails: number; total: number }>({
    queryKey: ["/api/dialer/unread-counts"],
    enabled: dialerHubVisible || dialerEnabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const dialerUnreadTotal = dialerUnreadCounts?.total ?? 0;
  useSessionTracker();
  // Task #251 — load the workspace's display timezone once, app-wide, so the
  // shared date formatters render every timestamp in the configured zone.
  useWorkspaceTimezone();

  // Clear the login-bounce circuit-breaker counter once the workspace
  // actually renders with a confirmed session. Previously this was reset
  // from Login.tsx (when it saw a valid session and auto-forwarded back),
  // but that created an infinite flash loop: the counter never reached 3
  // because it was zeroed on every pass through the Login page. By moving
  // the reset here — inside AuthenticatedApp, which only runs when useAuth()
  // returns a real user — we guarantee the counter only resets when the
  // workspace is genuinely reachable, so a persistent SESSION_EXPIRED blip
  // correctly drives the user to the login form via the ?fresh=1 path.
  useEffect(() => {
    if (user) sessionStorage.removeItem("login_redirect_count_v1");
  }, [user]);

  // Register the global view-as setter so components deep in the tree (e.g.
  // UsersAccessDialog "Log in as" button) can trigger impersonation without
  // prop-drilling through AuthenticatedRouter.
  useEffect(() => {
    registerViewAsSetter((id) => setViewAsUserId(id));
  }, []);

  // Sync impersonation state with global module so all API calls use the impersonated user.
  //
  // We rotate the primary session ID at every view-as start/stop boundary
  // (Task #587 — session-fixation hardening). The fetch is fire-and-forget
  // because the rotated cookie is set by the server response and the
  // browser will pick it up automatically — `setImpersonatedUser` runs
  // first so any in-flight request from the rotation itself doesn't carry
  // a stale view-as header.
  useEffect(() => {
    setImpersonatedUser(viewAsUserId || null);
    apiRequest("POST", "/api/auth/rotate-session").catch(() => {
      // Best-effort: a transient failure here just means the privilege
      // change rides on the previous cookie until the next rotation, no
      // user-visible regression.
    });
    // Task #12 — Invalidate all queries when impersonation changes so data
    // refreshes for the new user. Also runs when impersonation ends so
    // stale impersonated data isn't served back to the admin's own session.
    queryClient.invalidateQueries();
  }, [viewAsUserId]);

  const { data: profile, isLoading: profileLoading } = useQuery<UserProfile>({
    queryKey: ["/api/profile"],
    enabled: !!user,
  });

  // Fetch the real (non-impersonated) profile so admin controls stay visible
  // even when acting as a non-admin user. This query intentionally skips impersonation headers.
  const { data: realProfile } = useQuery<UserProfile>({
    queryKey: ["/api/profile-real"],
    queryFn: async () => {
      const res = await fetch("/api/profile", { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!user,
  });

  const { data: allUsers = [] } = useQuery<UserWithProfile[]>({
    queryKey: ["/api/users"],
    enabled: !!user && realProfile?.role?.toLowerCase() === "admin",
    queryFn: async () => {
      const res = await fetch("/api/users", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  // Fetch linked talent for the real user — only needed when admin might toggle to talent view.
  const { data: linkedTalent } = useQuery<Talent | null>({
    queryKey: ["/api/talent/by-user", user?.id],
    enabled: !!user?.id && realProfile?.role?.toLowerCase() === "admin",
    retry: false,
    select: (data: any) => data ?? null,
  });

  // Detect if a super admin is viewing this workspace via platform impersonation
  const { data: platformStatus } = useQuery<{
    platformImpersonating: boolean;
    platformAdminName?: string;
  }>({
    queryKey: ["/api/platform-session-status"],
    refetchInterval: 30000,
  });

  const stopPlatformImpersonation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/platform/stop-impersonation"),
    onSuccess: () => {
      window.location.href = "/platform/dashboard";
    },
  });

  const { permissions, isLoading: permissionsLoading } = useEffectivePermissions({
    viewAsUserId: viewAsUserId || undefined,
    enabled: !!user,
  });

  // Must be called before any early returns (Rules of Hooks)
  const isMobile = useIsMobile();

  // Public pages that render without authentication — must come before any
  // auth/loading gate so unauthenticated visitors see the page immediately.
  if (window.location.pathname === "/beta") {
    return <BetaApplyPage />;
  }

  // OAuth callback must render even for unauthenticated users — Google
  // redirects back here after the consent flow, potentially before the
  // session cookie is fully established in the new tab.
  if (window.location.pathname === "/oauth/callback") {
    return <OAuthCallbackPage />;
  }

  // Task #253 — wait only for the auth check first. This must come before the
  // profile/permissions gate: the permissions query retries for ~15s on a 401,
  // so blocking on `permissionsLoading` here would hang an unauthenticated
  // visitor on a blank spinner instead of redirecting them promptly.
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    // Task #253 — AuthenticatedApp only mounts at `/w/<slug>/...` URLs (via
    // WorkspaceRouteHandler). A missing user here is either a genuine
    // unauthenticated visitor at a deep workspace URL or a transient session
    // blip right after an OAuth round-trip. Never render the marketing Landing
    // here — redirect to login with a returnTo so the user comes right back.
    return <LoginRedirect />;
  }

  // User is authenticated — now wait for their profile + permissions to load.
  if (profileLoading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const userRole = ((profile?.role?.toLowerCase() || "sales_rep") as UserRole);
  // Use the real (non-impersonated) profile to determine admin status so the
  // "Act as..." control stays visible even while impersonating a non-admin.
  const isAdmin = realProfile?.role?.toLowerCase() === "admin";

  const selectedUser = allUsers.find(u => u.id === viewAsUserId);
  const viewAsUserRole = selectedUser?.profile?.role?.toLowerCase() as UserRole | undefined;

  // Onboarding: only the user's own profile drives the wizard (never the
  // impersonated one). The wizard itself handles users who lack permission
  // to perform some/all required steps (it auto-skips those and persists
  // completion), so we don't gate visibility on permissions here.
  const canManageTalent = permissions["adminPanel.feature.manageTalent"] === true;
  const canManageClientTypes = permissions["adminPanel.feature.manageClientTypes"] === true;
  const canManageRegions = permissions["adminPanel.feature.manageRegions"] === true;
  const canManageEventOfferings = permissions["adminPanel.feature.manageEventOfferings"] === true;
  const canManageBrand = permissions["adminPanel.feature.brandingSettings"] === true;
  const showOnboardingWizard =
    !!realProfile &&
    !realProfile.onboardingCompletedAt &&
    !viewAsUserId;

  const sidebarStyle = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  } as React.CSSProperties;

  return (
    /* Cmd-Shift-K (Ctrl-Shift-K) command palette is intentionally mounted
       inside the post-auth branch of AuthenticatedApp — *after* the
       `if (!user) return <Landing />` early-return above — so the keydown
       listener never runs for unauthenticated visitors hitting
       `/w/:workspace/...`. The listener inside CommandPaletteProvider also
       ignores Cmd-Shift-K keystrokes that originate inside text inputs /
       textareas / contenteditable surfaces. Plain Cmd-K is owned by the
       sidebar quick-find (see global-quick-find.tsx). */
    <VoiceAssistantStateProvider>
    <CommandPaletteProvider>
    <SidebarProvider style={sidebarStyle}>
      <div className="flex h-screen w-full">
        <AppSidebar viewAsUserId={viewAsUserId || null} onSwitchBack={() => setViewAsUserId("")} />
        <div className="flex flex-col flex-1 overflow-hidden">
          <header className="flex items-center h-14 px-4 border-b border-border bg-background shrink-0 gap-4">
            <SidebarTrigger className="hidden md:flex" data-testid="button-sidebar-toggle" />
            
            <HeaderNav permissions={permissions} />
            
            <div className="flex items-center gap-3 ml-auto">
              <InboxBell />
              {permissions["calendarHub.module.visible"] === true && (
                <Link href="/calendars">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label="Calendar Hub"
                    title="Calendar Hub"
                    data-testid="header-nav-calendarHub"
                  >
                    <CalendarDays className="h-4 w-4" />
                  </Button>
                </Link>
              )}
              {dialerHubVisible && (
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label="Dialer Hub"
                    title="Dialer Hub"
                    onClick={() => setDialerHubOpen(true)}
                    data-testid="header-nav-dialerHub"
                  >
                    <Phone className="h-4 w-4" />
                  </Button>
                  {dialerUnreadTotal > 0 && (
                    <span
                      className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-red-500 pointer-events-none"
                      data-testid="badge-dialer-unread"
                    />
                  )}
                </div>
              )}
              <NotificationBell />
              {platformStatus?.platformImpersonating && (
                <div className="flex items-center gap-2 px-2 py-1 bg-red-100 dark:bg-red-950/40 rounded-lg border border-red-400 dark:border-red-700">
                  <ShieldAlert className="h-3 w-3 sm:h-4 sm:w-4 text-red-600 dark:text-red-400 shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-red-700 dark:text-red-300 truncate hidden sm:block">
                    Super Admin View
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs border-red-400 text-red-700 hover:bg-red-200 dark:border-red-600 dark:text-red-300 dark:hover:bg-red-900/50 bg-white dark:bg-transparent shrink-0"
                    onClick={() => stopPlatformImpersonation.mutate()}
                    disabled={stopPlatformImpersonation.isPending}
                    data-testid="button-exit-platform-impersonation"
                  >
                    {stopPlatformImpersonation.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <>
                        <LogOut className="h-3 w-3 mr-1" />
                        <span>Switch Back</span>
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Admin ↔ Talent view toggle — mobile only, shown when admin has a linked talent profile */}
              {isAdmin && linkedTalent && isMobile && (
                <button
                  type="button"
                  onClick={() => setTalentViewActive((v) => !v)}
                  aria-label={talentViewActive ? "Switch to Admin View" : "Switch to Talent View"}
                  data-testid="button-mobile-admin-talent-toggle"
                  className={`flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-medium transition-colors ${talentViewActive ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}
                >
                  <SwitchCamera className="h-3.5 w-3.5" />
                  <span>{talentViewActive ? "Talent" : "Admin"}</span>
                </button>
              )}
              <AiCreditPill isAdmin={isAdmin} />
              <ThemeToggle />
            </div>
          </header>
          {isAdmin && viewAsUserId && selectedUser && isMobile && !talentViewActive && (
            <div className="md:hidden px-3 py-1.5 bg-orange-100 dark:bg-orange-900/30 border-b border-orange-300 dark:border-orange-700 flex items-center gap-2">
              <Eye className="h-3 w-3 text-orange-600 dark:text-orange-400" />
              <span className="text-xs font-medium text-orange-700 dark:text-orange-300 flex-1">
                Acting as: {selectedUser.firstName} {selectedUser.lastName}
              </span>
              <Badge variant="outline" className="text-xs border-orange-400 text-orange-600 dark:text-orange-400">
                {viewAsUserRole}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1 text-xs text-orange-700 dark:text-orange-300"
                onClick={() => setViewAsUserId("")}
                data-testid="button-exit-impersonation-mobile"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          <main className="flex-1 overflow-auto bg-muted/30 pb-safe-or-0 md:pb-0" style={{ paddingBottom: isMobile ? 'calc(56px + env(safe-area-inset-bottom, 0px))' : undefined }}>
            <EmailVerificationBanner />
            <LiteUpsellBanner />
            <AuthenticatedRouter 
              userId={user.id}
              viewAsUserId={viewAsUserId}
            />
          </main>
          <LiteCardRequiredGate />
          <DialerHost />
          {dialerHubVisible && (
            <DialerListener dialerEnabled={dialerEnabled} />
          )}
          <DialerHub open={dialerHubOpen} onOpenChange={setDialerHubOpen} />
        </div>
      </div>
      <MobileBottomNav
        permissions={permissions}
        user={user}
        talentView={isMobile && (
          (isAdmin && !!linkedTalent && talentViewActive) ||
          (!isAdmin && profile?.role?.toLowerCase() === "talent")
        )}
        linkedTalent={linkedTalent}
        isAdmin={isAdmin}
        viewAsUserId={viewAsUserId}
        setViewAsUserId={setViewAsUserId}
        allUsers={allUsers}
      />
      {showOnboardingWizard && realProfile && isAdmin && (
        <OnboardingWizard
          profile={realProfile}
          canManageTalent={canManageTalent}
          canManageClientTypes={canManageClientTypes}
          canManageRegions={canManageRegions}
          canManageEventOfferings={canManageEventOfferings}
          canManageBrand={canManageBrand}
        />
      )}
      {showOnboardingWizard && realProfile && !isAdmin && (
        <UserSetupWizard profile={realProfile} />
      )}
      {/* Security training gate: blocks the app for users who still owe required attestations.
          Skipped during admin impersonation so the impersonating admin doesn't see the impersonated
          user's gate (the gate uses the real session user, but the impersonation UX should be
          uninterrupted). The component itself no-ops when enforcement is off or all required
          articles have been acknowledged. */}
      {!viewAsUserId && <GettingStartedDrawer isAdmin={isAdmin} />}
      {!viewAsUserId && <SecurityTrainingGate />}
      {/* Global goal-hit celebrations: fires confetti + toast from anywhere
          when a daily metric crosses 100%. Skipped while impersonating so
          admins don't get notifications based on another user's snapshot. */}
      {!viewAsUserId && <GlobalGamificationCelebrations />}
      <VoiceAssistantWidget />
      <InstallPromptBanner />
      <CommandPalette />
    </SidebarProvider>
    </CommandPaletteProvider>
    </VoiceAssistantStateProvider>
  );
}

// Wrapper that provides workspace context for authenticated routes
function WorkspaceApp({ workspaceSlug }: { workspaceSlug: string }) {
  return (
    <WorkspaceProvider workspaceSlug={workspaceSlug}>
      <AuthenticatedAppShell />
    </WorkspaceProvider>
  );
}

// Route handler for /w/:workspace/* paths.
// Task #128 — session-vs-URL guard. If the session is bound to a different
// workspace (or no workspace at all), redirect the browser to the correct
// destination rather than letting the user operate in the wrong tenant.
// We use a module-level Map to cache the result per sessionStorage key so the
// API call only fires once per page load, not on every navigation within the
// same workspace.
const _sessionWorkspaceCache: { slug: string | null | undefined } = { slug: undefined };

function WorkspaceRouteHandler() {
  const [location] = useLocation();
  const [sessionSlug, setSessionSlug] = useState<string | null | undefined>(
    _sessionWorkspaceCache.slug,
  );

  // Extract workspace slug from URL
  const match = location.match(/^\/w\/([^/]+)/);
  const urlSlug = match ? match[1] : null;

  useEffect(() => {
    if (_sessionWorkspaceCache.slug !== undefined) {
      // Already resolved this page load — nothing to do.
      setSessionSlug(_sessionWorkspaceCache.slug);
      return;
    }
    fetch("/api/auth/my-workspace", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        const resolved = data.slug || null;
        _sessionWorkspaceCache.slug = resolved;
        setSessionSlug(resolved);
      })
      .catch(() => {
        // On network error fall through; the API's 403 responses will handle
        // access control server-side.
        _sessionWorkspaceCache.slug = null;
        setSessionSlug(null);
      });
  }, []);

  if (!urlSlug) {
    // Task #124 — no implicit fallback; root redirect resolves the
    // signed-in user's workspace.
    return <Redirect to="/" />;
  }

  // While the session workspace is still being resolved, render the app
  // normally — the server will enforce isolation via the global middleware.
  // We only redirect client-side once we have a confirmed mismatch.
  if (sessionSlug !== undefined && sessionSlug !== null && sessionSlug !== urlSlug) {
    // Session is bound to a different workspace — redirect to the correct one.
    return <Redirect to={`/w/${sessionSlug}/`} />;
  }

  // Use nested router for workspace routes (strips /w/:workspace prefix)
  return (
    <Router base={`/w/${urlSlug}`}>
      <WorkspaceApp workspaceSlug={urlSlug} />
    </Router>
  );
}

// Redirect root to workspace
function RootRedirect() {
  const { user, isLoading } = useAuth();
  const [workspaceSlug, setWorkspaceSlug] = useState<string | null | undefined>(undefined);
  const [wsLoading, setWsLoading] = useState(false);

  useEffect(() => {
    if (user && workspaceSlug === undefined) {
      setWsLoading(true);
      fetch("/api/auth/my-workspace", { credentials: "include" })
        .then((r) => r.json())
        // Task #128 — fail closed. null slug = no workspace bound on session.
        // Could mean 0 workspaces (→ /no-workspace-access) or multiple
        // workspaces that still require selection (→ /select-workspace).
        // The server returns requiresWorkspaceSelection: true to distinguish.
        .then((data) => {
          if (data.requiresWorkspaceSelection) {
            window.location.replace("/select-workspace");
          } else if (!data.slug) {
            window.location.replace("/no-workspace-access");
          } else {
            setWorkspaceSlug(data.slug);
          }
        })
        .catch(() => setWorkspaceSlug(null))
        .finally(() => setWsLoading(false));
    }
  }, [user, workspaceSlug]);

  if (isLoading || (user && wsLoading)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (user && workspaceSlug) {
    return <Redirect to={`/w/${workspaceSlug}/`} />;
  }

  // Not authenticated: show landing page
  return <Landing />;
}

function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="warehouse-theme">
        <TooltipProvider>
          <PendingEmailDraftsProvider>
          <DynamicFavicon />
          <Switch>
            {/* Platform Admin Routes — Task #124: the Clarik tenant shell
                (`/w/clarik`) is gone. Every `/platform/*` page renders
                inside `PlatformLayout` (workspace-style sidebar + top
                bar) and is bound directly to its real component. */}
            <Route path="/platform/login" component={PlatformLogin} />
            <Route path="/platform" component={() => <Redirect to="/platform/dashboard" />} />
            <Route path="/platform/" component={() => <Redirect to="/platform/dashboard" />} />
            <Route path="/platform/dashboard" component={PlatformDashboard} />
            <Route path="/platform/admin" component={PlatformAdminHub} />
            <Route path="/platform/workspaces" component={PlatformWorkspacesHub} />
            <Route path="/platform/plans" component={PlatformPlansHub} />
            <Route path="/platform/imports" component={PlatformImportsHub} />
            <Route path="/platform/operations" component={PlatformOperationsHub} />
            <Route path="/platform/security" component={PlatformSecurityHub} />
            <Route path="/platform/config" component={PlatformConfigHub} />
            <Route path="/platform/tiers" component={PlatformTiers} />
            <Route path="/platform/coupons" component={PlatformCoupons} />
            <Route path="/platform/audit" component={PlatformAudit} />
            <Route path="/platform/financial-audit" component={PlatformFinancialAudit} />
            <Route path="/platform/lead-audit" component={PlatformLeadAudit} />
            <Route path="/platform/event-audit" component={PlatformEventAudit} />
            <Route path="/platform/seed-failures" component={PlatformSeedFailures} />
            <Route path="/platform/seed-failure-alerts" component={PlatformSeedFailureAlerts} />
            <Route path="/platform/honeytokens" component={PlatformHoneytokens} />
            <Route path="/platform/feedback" component={PlatformFeedback} />
            <Route path="/platform/backups" component={PlatformBackups} />
            <Route path="/platform/enhancements" component={PlatformEnhancements} />
            <Route path="/platform/help-kb" component={() => <Redirect to="/platform/ai-intelligence" />} />
            <Route path="/platform/imports-migrations" component={PlatformImportsMigrations} />
            <Route path="/platform/ai-import" component={PlatformAiImport} />
            <Route path="/platform/ai-assistants" component={PlatformAiAssistants} />
            <Route path="/platform/ai-agents" component={PlatformAiAgents} />
            <Route path="/platform/engineer-reports" component={PlatformEngineerReports} />
            <Route path="/platform/ai-agents/:agentId" component={PlatformAiAgentRuns} />
            <Route path="/platform/ai-intelligence" component={PlatformAiIntelligence} />
            <Route path="/platform/migrations" component={PlatformImportsMigrations} />
            <Route path="/platform/onboarding-imports" component={PlatformAiImport} />
            <Route path="/platform/encryption-migration" component={PlatformEncryptionMigration} />
            <Route path="/platform/settings" component={PlatformSettings} />
            <Route path="/platform/notification-settings" component={PlatformNotificationSettings} />
            <Route path="/platform/devices" component={PlatformDevices} />
            <Route path="/platform/pii-scans/:runId" component={PlatformPiiScans} />
            <Route path="/platform/pii-scans" component={PlatformPiiScans} />
            <Route path="/platform/secrets" component={PlatformSecrets} />
            <Route path="/platform/email-verify" component={PlatformEmailVerify} />
            <Route path="/platform/waitlist" component={PlatformWaitlist} />
            <Route path="/platform/profiles" component={PlatformProfiles} />
            <Route path="/platform/communications" component={PlatformCommunications} />
            <Route path="/platform/approvals" component={PlatformBetaApprovals} />
            <Route path="/platform/gmail-read" component={PlatformGmailRead} />
            <Route path="/platform/account" component={PlatformAccount} />
            <Route path="/platform/email-settings" component={PlatformEmailSettings} />
            <Route path="/platform/prospects" component={PlatformProspects} />
            <Route path="/platform/customers" component={PlatformCustomers} />
            <Route path="/platform/invoices" component={PlatformInvoices} />
            <Route path="/platform/billing" component={PlatformBilling} />
            <Route path="/platform/infra" component={PlatformInfra} />
            <Route path="/platform/email" component={PlatformEmailHub} />
            <Route path="/platform/lead-gen" component={PlatformLeadGen} />
            <Route path="/platform/ip-allowlist" component={PlatformIpAllowlist} />
            <Route path="/platform/calls" component={PlatformCalls} />
            
            {/* Public Routes */}
            <Route path="/beta" component={BetaApplyPage} />
            <Route path="/welcome" component={Landing} />
            <Route path="/marketing/pricing" component={() => <Redirect to="/pricing" />} />
            <Route path="/pricing" component={MarketingPricingPage} />
            <Route path="/marketing/features" component={MarketingFeaturesPage} />
            <Route path="/marketing/about" component={MarketingAboutPage} />
            <Route path="/signup" component={Signup} />
            <Route path="/signup/success" component={SignupSuccess} />
            <Route path="/verify-email" component={VerifyEmail} />
            <Route path="/login" component={Login} />
            {/* Task #190 — first-login password reset + optional MFA enrollment */}
            <Route path="/first-login" component={FirstLogin} />
            {/* Task #219 — self-serve forgot/reset password */}
            <Route path="/forgot-password" component={ForgotPassword} />
            <Route path="/reset-password" component={ResetPassword} />
            {/* Task #128 — workspace session binding (module-scope lazy import above) */}
            <Route path="/select-workspace" component={() => (
              <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="w-6 h-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>}>
                <SelectWorkspacePage />
              </Suspense>
            )} />
            <Route path="/no-workspace-access" component={() => (
              <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 p-4">
                <div className="w-full max-w-md text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 mb-4">
                    <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">No workspace found</h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                    Your account isn't linked to a workspace. This can happen after a session refresh — try signing in again.
                  </p>
                  <div className="flex flex-col gap-3 items-center">
                    <a
                      href="/api/logout"
                      className="w-full max-w-xs inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
                      data-testid="link-retry-sign-in"
                      onClick={(e) => { e.preventDefault(); window.location.href = "/api/logout"; }}
                    >
                      Sign out &amp; try again
                    </a>
                    <a
                      href="/platform/login"
                      className="text-sm text-muted-foreground hover:text-primary hover:underline transition-colors"
                      data-testid="link-platform-login"
                    >
                      Platform admin? Sign in here
                    </a>
                  </div>
                </div>
              </div>
            )} />
            <Route path="/invoice/:id" component={PublicInvoicePage} />
            <Route path="/sign/:token" component={SignPage} />
            <Route path="/platform-sign/:token" component={PlatformSignPage} />
            <Route path="/portal/:token" component={PortalPage} />
            <Route path="/share/call-sheet/:token" component={PublicCallSheetPage} />
            <Route path="/artist/share/:token" component={PublicArtistPage} />
            <Route path="/artist/:slug" component={PublicArtistPage} />
            <Route path="/review/:workspaceId/:talentId/:invoiceId" component={PublicReviewPage} />
            {/* Permanent alias: old review URL kept for previously-emailed links */}
            <Route path="/review/:workspaceId/:entertainerId/:invoiceId" component={PublicReviewPage} />
            <Route path="/request-event/:slug" component={RequestEventPage} />
            <Route path="/request-event" component={RequestEventPage} />
            {/* Permanent alias: old request-show URL kept for previously-shared links */}
            <Route path="/request-show/:slug" component={RequestEventPage} />
            <Route path="/request-show" component={RequestEventPage} />
            <Route path="/book/:slug/:formSlug" component={RequestEventPage} />
            <Route path="/book/:slug" component={RequestEventPage} />
            <Route path="/privacy" component={PrivacyPolicyPage} />
            <Route path="/terms" component={TermsOfServicePage} />
            <Route path="/data-deletion" component={DataDeletionPage} />
            <Route path="/help/:slug" component={HelpPage} />
            <Route path="/help" component={HelpPage} />
            <Route path="/help-kb/:slug" component={HelpKbArticlePage} />
            <Route path="/help-kb" component={HelpKbArticlePage} />
            <Route path="/security/:slug" component={SecurityPage} />
            <Route path="/security" component={SecurityPage} />
            <Route path="/trust/:slug" component={TrustPage} />
            <Route path="/trust" component={TrustPage} />
            <Route path="/policies/:slug" component={PoliciesPage} />
            <Route path="/policies" component={PoliciesPage} />
            <Route path="/connect-sms/:token" component={ConnectSmsPage} />
            <Route path="/connect-calendar/:token" component={ConnectCalendarPage} />
            <Route path="/oauth/callback" component={OAuthCallbackPage} />
            <Route path="/mfa-challenge" component={MfaChallengePage} />
            
            {/* Workspace Routes - Path-based multi-tenancy */}
            <Route path="/w/:workspace/*?" component={WorkspaceRouteHandler} />
            
            {/* Root - redirect to workspace or show landing */}
            <Route path="/" component={RootRedirect} />
            
            {/* Legacy bare-path aliases — Task #124 removed the
                hardcoded `warehouse-productions` fallback. Route them
                through `/` so RootRedirect resolves the signed-in
                user's workspace (or shows the landing page). */}
            <Route path="/dashboard" component={() => <Redirect to="/" />} />
            <Route path="/leads" component={() => <Redirect to="/" />} />
            <Route path="/clients" component={() => <Redirect to="/" />} />
            <Route path="/invoices" component={() => <Redirect to="/" />} />
            <Route path="/admin" component={() => <Redirect to="/" />} />
            <Route path="/account/security" component={() => <Redirect to="/" />} />
            <Route path="/account/billing" component={() => <Redirect to="/" />} />
            <Route path="/inbox" component={() => <Redirect to="/" />} />
            <Route path="/email-hub" component={() => <Redirect to="/" />} />
            
            {/* Fallback */}
            <Route component={NotFound} />
          </Switch>
          <GmailReconnectPrompt />
          <ServerUnavailableBanner />
          <BillingSuspendedGate />
          <Toaster />
          </PendingEmailDraftsProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
